import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Toggle from '../components/Toggle'
import ChevronRightIcon from '../icons/ChevronRightIcon'
import Exercise from './Exercise'
import Intro from './Intro'
import exercises from '../exercises.json'

const defaultSeconds = 64

function getExerciseProps (step, currentStep) {
  let color = ''

  // Tailwind whitelist... bg-blue-300 bg-blue-400 bg-red-600 bg-yellow-300
  // text-blue-300 text-blue-400 text-red-600 text-yellow-300
  if (step === 2) {
    color = 'yellow-300'
  } else if (step === 3) {
    color = 'red-600'
  } else if (step === 4) {
    color = 'blue-300'
  }

  return { active: step === currentStep, color, step }
}

function getNextStepText (step) {
  switch (step) {
    case 2:
      return 'Next'
    case 3:
      return 'Next'
    case 4:
      return 'Go Again!'
    default:
      return ''
  }
}

function shuffle (array, iteration) {
  let currentIndex = array.length

  let randomIndex
  let temporaryValue

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex -= 1

    temporaryValue = array[currentIndex]
    array[currentIndex] = array[randomIndex]
    array[randomIndex] = temporaryValue
  }

  return [...array]
}

export default function NarrowApp () {
  const [iteration, setIteration] = useState(1)
  const [seconds, setSeconds] = useState(defaultSeconds)
  const [step, setStep] = useState(1)
  const [timer, setTimer] = useState(true)

  const coolExercise = useMemo(() => {
    return shuffle(exercises.cool, iteration)[0]
  }, [iteration])

  const moveExercise = useMemo(() => {
    return shuffle(exercises.move, iteration)[0]
  }, [iteration])

  const warmExercise = useMemo(() => {
    return shuffle(exercises.warm, iteration)[0]
  }, [iteration])

  const navigateNext = useCallback(() => {
    const nextStep = step >= 4 ? 1 : step + 1

    setSeconds(defaultSeconds)
    setStep(nextStep)

    if (nextStep === 2) {
      setIteration(i => i + 1)
    }
  }, [step])

  useEffect(() => {
    if (step === 1 || !timer) {
      return
    }

    if (seconds <= 0) {
      navigateNext()
      return
    }

    const milliseconds = seconds > 60 ? 500 : 1000

    const timeout = setTimeout(() => {
      setSeconds(s => s - 1)
    }, milliseconds)

    return () => clearInterval(timeout)
  }, [seconds, step, timer, navigateNext])

  if (step === 1) {
    return <Intro onNavigateNext={navigateNext} />
  }

  return (
    <div className='border border-black flex flex-col h-full overflow-hidden w-full'>
      <div
        id='main'
        className='flex flex-1 flex-col overflow-hidden relative w-full z-10 lg:flex-row'
        tabIndex='0'
      >
        <Exercise
          exercise={warmExercise}
          position='left'
          seconds={seconds}
          timer={timer}
          type='warm'
          {...getExerciseProps(2, step)}
        />
        <Exercise
          exercise={moveExercise}
          position='center'
          seconds={seconds}
          timer={timer}
          type='move'
          {...getExerciseProps(3, step)}
        />
        <Exercise
          exercise={coolExercise}
          position='right'
          seconds={seconds}
          timer={timer}
          type='cool'
          {...getExerciseProps(4, step)}
        />
      </div>
      <div
        id='navbar'
        className='bg-white border-black border-t flex flex-shrink-0 h-14 relative shadow-xl z-20 sm:h-16 md:h-20'
      >
        <div className='border-black border-r flex h-14 items-center justify-center w-1/2 sm:h-16 md:h-20'>
          <span className='leading-6 mr-3 text-xl sm:leading-7 sm:mr-4 sm:text-2xl lg:leading-8 lg:mr-5 lg:text-3xl'>Timer</span>
          <Toggle value={timer} onToggle={() => setTimer(t => !t)} />
        </div>
        <div className='w-1/2'>
          <button
            className={classNames('bg-white flex h-14 items-center justify-center transition-150 w-full hover:text-white focus:outline-none sm:h-16 md:h-20', {
              'text-red-600 hover:bg-red-600': step === 2,
              'text-blue-400 hover:bg-blue-400': step === 3,
              'text-green-400 hover:bg-green-400': step === 4
            })}
            type='button'
            onClick={() => navigateNext()}
          >
            <span className='leading-6 mr-3 text-xl sm:leading-7 sm:mr-4 sm:text-2xl lg:leading-8 lg:mr-5 lg:text-3xl'>{getNextStepText(step)}</span>
            <ChevronRightIcon className='block fill-current h-6 w-auto sm:h-7 lg:h-8' />
          </button>
        </div>
      </div>
    </div>
  )
}
